import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useInstitution } from '../../contexts/InstitutionContext';
import {
  getSessionWorkspace,
  hasAnyInstitutionStudentAccess,
  resolveWorkspacePath,
  STUDENT_HOME_PATH,
} from '../../lib/institutionOnboarding';
import { FullPageLoader } from '../ui/page-loader';

/** Classroom workspace students may still take assigned exams and view results. */
const CLASSROOM_WORKSPACE_ALLOWED_PREFIXES = ['/exam/', '/practice/results/'];

function isClassroomWorkspaceRoute(pathname) {
  return CLASSROOM_WORKSPACE_ALLOWED_PREFIXES.some((prefix) => pathname.startsWith(prefix));
}

/**
 * Institution students must pick a workspace (/home) before using platform routes.
 * Classroom workspace redirects to their portal slug (except assignment exam flows).
 */
export function StudentPlatformGuard({ children }) {
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();
  const { studentMemberships, allMemberships, loading: instLoading } = useInstitution();

  if (authLoading || instLoading) {
    return <FullPageLoader message="Loading" />;
  }

  if (!user || user.role === 'admin') return children;

  if (!hasAnyInstitutionStudentAccess(allMemberships)) return children;

  const workspace = getSessionWorkspace();
  if (!workspace) {
    return <Navigate to={STUDENT_HOME_PATH} replace />;
  }

  if (workspace !== 'platform') {
    if (!isClassroomWorkspaceRoute(location.pathname)) {
      const dest = resolveWorkspacePath(workspace, studentMemberships);
      if (dest && dest !== '/dashboard') {
        return <Navigate to={dest} replace />;
      }
    }
  }

  return children;
}

export function StudentHomeHubEntry({ children }) {
  const { user, loading: authLoading } = useAuth();
  const { studentMemberships, allMemberships, loading: instLoading } = useInstitution();

  if (authLoading || instLoading) {
    return <FullPageLoader message="Loading" />;
  }

  if (user && hasAnyInstitutionStudentAccess(allMemberships)) {
    const workspace = getSessionWorkspace();
    if (!workspace) {
      return <Navigate to={STUDENT_HOME_PATH} replace />;
    }
  }

  return children;
}
